import { useCallback, useEffect, useRef, useState } from 'react';
import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import type { Upsell } from '@alltrails/types/Upsell';
import { type RootState, useAppStore } from '../utils/redux';

// Covers the endpoint's p99 plus network round trip.
export const UPSELL_OPEN_DELAY_MS = 400;

type State = {
  isOpen: boolean;
  serverUpsellContent?: Upsell;
};

type Options = {
  // Wait for in-flight content before opening. Only for surfaces that open on page load, which start
  // roughly 10ms after the request and so always lose the race. A wait after a click is perceptible.
  delayOpen?: boolean;
  // For surfaces that are open for as long as they are mounted, so they snapshot on their first
  // render rather than after a state update.
  initiallyOpen?: boolean;
};

const findUpsell = (state: RootState, trigger: UpsellTrigger) => state.upsellContent?.data?.find(upsell => upsell.triggerKey === trigger);

// Opens the upsell and holds the copy from the store at that moment, so a later
// response can't swap it under the user. Reads via the store rather than a
// selector so store updates do not re-render a closed surface.
export default function useUpsellContent(trigger: UpsellTrigger, { delayOpen = false, initiallyOpen = false }: Options = {}) {
  const store = useAppStore();
  const [{ isOpen, serverUpsellContent }, setState] = useState<State>(() => ({
    isOpen: initiallyOpen,
    serverUpsellContent: initiallyOpen ? findUpsell(store.getState(), trigger) : undefined
  }));
  const openTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
      }
    };
  }, []);

  const setIsOpen = useCallback(
    (nextIsOpen: boolean) => {
      clearTimeout(openTimerRef.current);

      if (!nextIsOpen) {
        setState({ isOpen: false, serverUpsellContent: undefined });
        return;
      }

      const loaded = findUpsell(store.getState(), trigger);

      if (loaded || !delayOpen) {
        setState({ isOpen: true, serverUpsellContent: loaded });
        return;
      }

      // Reading the store inside the timer is the point of the wait: it picks up whatever arrived.
      openTimerRef.current = setTimeout(() => {
        setState({ isOpen: true, serverUpsellContent: findUpsell(store.getState(), trigger) });
      }, UPSELL_OPEN_DELAY_MS);
    },
    [delayOpen, store, trigger]
  );

  return { isOpen, serverUpsellContent, setIsOpen };
}
